import React, { createContext, PropsWithChildren, ReactElement, useContext } from 'react';
import { Box, Button, Link, Paper, Theme, Typography } from '@mui/material';

import { ParaLogo } from '@paragon/client-tools';
import { InlineNotification, InlineNotificationType } from '@paragon/notification-tools';

import { getLogOutReason } from '../../logOutReason';
import { ErrorForm } from './components/ErrorForm';
import { LoginTypography } from './components/LoginTypography';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { ErrorBarrier } from './ErrorBarrier';

export const TERMS_LINK = 'https://paragontruss.com/terms';
export const PRIVACY_LINK = 'https://paragontruss.com/privacy';

interface FlashController {
  readonly set: (type: InlineNotificationType, text: string) => void;
  readonly clear: () => void;
}

export const FlashContext = createContext<FlashController>({} as FlashController);
export const useLoginFlash = () => useContext(FlashContext);

export interface LoginScreenProps extends PropsWithChildren {
  readonly themeOverride?: Theme;
  readonly logoOverride?: ReactElement;

  readonly clientLogoUrl?: string;
  readonly clientName?: string;

  readonly hideTermsAndPrivacy?: boolean;
}

interface State {
  readonly didCrash: boolean;

  readonly flashText: string | null;
  readonly flashType: InlineNotificationType;
}

/**
 * Renders a full screen centered panel with Paragon logo and client information.
 *
 * Used for login and account management screens across all our clients by the login-tools library. Suitable for
 *   separate use whenever you need a full screen panel for interaction before a user is logged in.
 */
export class LoginScreen extends React.Component<LoginScreenProps, State> {
  state: State;

  constructor(props: LoginScreenProps) {
    super(props);

    const [flashType, flashText] = getLogOutReason();

    this.state = {
      didCrash: false,

      flashText,
      flashType: flashType ?? 'info',
    };
  }

  /**
   * An object which can be used to control the flash message in the login panel.  This can be accessed using the
   *   `useLoginFlash` hook.
   */
  flashController = {
    set: (flashType: InlineNotificationType, flashText: string) => {
      this.setState({ flashType, flashText });
    },
    clear: () => {
      this.setState({ flashText: null });
    },
  };

  getFlash(): [InlineNotificationType, string] | null {
    if (this.state.flashText) {
      return [this.state.flashType, this.state.flashText];
    }

    return null;
  }

  handleError = (error: unknown) => {
    console.error(error);

    this.setState({ didCrash: true });
  };

  renderClientInfo() {
    const { clientLogoUrl, clientName } = this.props;
    if (clientLogoUrl == null || clientName == null) return null;
    return (
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '100%',
          alignItems: 'center',
          padding: 1,
          justifyContent: 'center',
          marginTop: 2,
          whiteSpace: 'nowrap',
          border: 'solid #595959 1px',
          borderRadius: 1,
        }}
      >
        <Box component="img" sx={{ mr: 1, width: 50, height: 50 }} src={clientLogoUrl} />
        <Typography variant="h1">{clientName}</Typography>
      </Box>
    );
  }

  render() {
    const flash = this.getFlash();

    return (
      <FlashContext.Provider value={this.flashController}>
        <ErrorBarrier onError={this.handleError}>
          <MaintenanceBanner />
          <Box sx={{ margin: 'auto', overflow: 'auto' }}>
            <Paper
              sx={{
                background: 'none',
                backgroundImage: 'none',
                padding: 4,
                pt: 0,
                pb: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              elevation={0}
            >
              {this.props.logoOverride || (
                <Link href="https://www.paragontruss.com">
                  <ParaLogo
                    sx={{ display: { sm: 'none' } }}
                    width={120}
                    height={120}
                    viewBox="0 0 32 32"
                    fill="#F4D367"
                  />
                  <ParaLogo
                    sx={{ display: { xs: 'none', sm: 'flex', md: 'none' } }}
                    width={150}
                    height={150}
                    viewBox="0 0 32 32"
                    fill="#F4D367"
                  />
                  <ParaLogo
                    sx={{ display: { xs: 'none', md: 'flex' } }}
                    width={230}
                    height={230}
                    viewBox="0 0 32 32"
                    fill="#F4D367"
                  />
                </Link>
              )}
              {!this.props.logoOverride && (
                <Link href="https://www.paragontruss.com" color="inherit" underline="none">
                  <Typography
                    variant="h1"
                    sx={{
                      mt: { xs: -4, sm: -5, md: -7 },
                      fontSize: { xs: '1.5em', sm: '2em', md: '3em' },
                      width: '100%',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    P A R A G O N
                  </Typography>
                </Link>
              )}
              {this.renderClientInfo()}

              {flash && (
                <LoginTypography>
                  <InlineNotification type={flash[0]}>{flash[1]}</InlineNotification>
                </LoginTypography>
              )}

              {this.state.didCrash ? <ErrorForm /> : this.props.children}
            </Paper>
            {!this.props.hideTermsAndPrivacy && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button href={TERMS_LINK} sx={{ m: 1, bgcolor: 'transparent', color: 'text.primary' }}>
                  Terms
                </Button>
                <Button href={PRIVACY_LINK} sx={{ m: 1, bgcolor: 'transparent', color: 'text.primary' }}>
                  Privacy
                </Button>
              </Box>
            )}
          </Box>
        </ErrorBarrier>
      </FlashContext.Provider>
    );
  }
}
